import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Spin, Switch } from 'antd';
import { ExperimentOutlined, SafetyOutlined, SaveOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { useNewGui } from '../../hooks/useNewGui';

export interface IpaPublicConfig {
  apiBase: string;
  baseDn: string;
  bindUser: string;
  verifyTls: boolean;
  passwordConfigured: boolean;
  configured: boolean;
}

interface Props {
  isSuperadmin: boolean;
}

const FreeIpaSettingsTab: React.FC<Props> = ({ isSuperadmin }) => {
  const useNew = useNewGui();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cfg, setCfg] = useState<IpaPublicConfig | null>(null);
  const [apiBase, setApiBase] = useState('');
  const [baseDn, setBaseDn] = useState('');
  const [bindUser, setBindUser] = useState('');
  const [bindPassword, setBindPassword] = useState('');
  const [verifyTls, setVerifyTls] = useState(true);

  const applyCfg = useCallback((data: IpaPublicConfig) => {
    setCfg(data);
    setApiBase(data.apiBase || '');
    setBaseDn(data.baseDn || '');
    setBindUser(data.bindUser || '');
    setVerifyTls(data.verifyTls !== false);
    setBindPassword('');
  }, []);

  const fetchIpa = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<IpaPublicConfig>('/api/system-settings/ipa');
      applyCfg(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load FreeIPA settings');
    } finally {
      setLoading(false);
    }
  }, [applyCfg]);

  useEffect(() => {
    if (isSuperadmin) fetchIpa();
    else setLoading(false);
  }, [isSuperadmin, fetchIpa]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body: Record<string, unknown> = {
        apiBase: apiBase.trim(),
        baseDn: baseDn.trim(),
        bindUser: bindUser.trim(),
        verifyTls,
      };
      if (bindPassword.trim()) body.bindPassword = bindPassword.trim();
      const { data } = await api.put<IpaPublicConfig>('/api/system-settings/ipa', body);
      applyCfg(data);
      setSuccess('FreeIPA settings saved.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save FreeIPA settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      const { data } = await api.post<{ message: string }>('/api/system-settings/ipa/test');
      setSuccess(data.message || 'FreeIPA connection test succeeded');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'FreeIPA connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (!isSuperadmin) {
    return (
      <Alert
        type="info"
        showIcon
        message="FreeIPA configuration is superadmin-only."
        description="Ask a superadmin to configure FreeIPA connectivity under Configuration → FreeIPA."
      />
    );
  }

  if (loading) return <Spin tip="Loading FreeIPA settings..." />;

  const cardClass = useNew
    ? 'bg-surface border border-border-subtle rounded-lg p-5 shadow-sm'
    : 'bg-white rounded-lg shadow-sm p-6';
  const labelClass = useNew ? 'text-ink-primary' : undefined;
  const hintClass = useNew ? 'text-xs text-ink-secondary mt-1' : 'text-xs text-gray-400 mt-1';

  return (
    <div className="space-y-4 max-w-2xl">
      {error && <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} showIcon closable onClose={() => setSuccess(null)} />}

      <div className={cardClass}>
        <div className="flex items-center gap-2 mb-1">
          <SafetyOutlined className={useNew ? 'text-accent' : 'text-blue-600'} />
          <h2 className={useNew ? 'text-base font-semibold text-ink-primary' : 'text-lg font-semibold text-gray-700'}>
            FreeIPA
          </h2>
        </div>
        <p className={useNew ? 'text-sm text-ink-secondary mb-4' : 'text-sm text-gray-400 mb-4'}>
          Used for workspace-container user/group provisioning (IC-040). {cfg && !cfg.configured && (
            <span className="block mt-1 text-amber-600">Not fully configured yet — user/group creation will fail until all fields are set.</span>
          )}
        </p>

        <Form layout="vertical" disabled={!isSuperadmin}>
          <Form.Item
            label={<span className={labelClass}>API base URL</span>}
            required
            extra={<span className={hintClass}>FreeIPA server, e.g. https://ipa.trg.vlsiexpert.in — JSON-RPC API, not raw LDAP.</span>}
          >
            <Input
              value={apiBase}
              onChange={(e) => setApiBase(e.target.value)}
              placeholder="https://ipa.trg.vlsiexpert.in"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={<span className={labelClass}>Base DN</span>}
            extra={<span className={hintClass}>For reference only, e.g. dc=trg,dc=vlsiexpert,dc=in</span>}
          >
            <Input
              value={baseDn}
              onChange={(e) => setBaseDn(e.target.value)}
              placeholder="dc=trg,dc=vlsiexpert,dc=in"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={<span className={labelClass}>Service account username</span>}
            extra={<span className={hintClass}>A dedicated FreeIPA account with least-privilege user/group create rights — not the admin account.</span>}
          >
            <Input
              value={bindUser}
              onChange={(e) => setBindUser(e.target.value)}
              placeholder="svc-infraconsole"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={<span className={labelClass}>Service account password</span>}
            extra={
              <span className={hintClass}>
                {cfg?.passwordConfigured
                  ? 'A password is stored. Leave blank to keep it unchanged.'
                  : 'No password stored yet.'}
              </span>
            }
          >
            <Input.Password
              value={bindPassword}
              onChange={(e) => setBindPassword(e.target.value)}
              placeholder={cfg?.passwordConfigured ? '••••••••' : 'FreeIPA service account password'}
              autoComplete="new-password"
            />
          </Form.Item>
          <Form.Item
            label={<span className={labelClass}>Verify TLS certificate</span>}
            extra={<span className={hintClass}>Only disable for a known internal/self-signed CA.</span>}
          >
            <Switch checked={verifyTls} onChange={setVerifyTls} />
          </Form.Item>

          <div className="flex flex-wrap gap-2 mt-2">
            <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
              Save FreeIPA
            </Button>
          </div>
        </Form>
      </div>

      <div className={cardClass}>
        <h3 className={useNew ? 'text-sm font-semibold text-ink-primary mb-2' : 'text-base font-semibold text-gray-700 mb-2'}>
          Test connection
        </h3>
        <p className={hintClass + ' mb-3'}>
          Authenticates against FreeIPA using the currently saved credentials (save first if you just edited the form).
        </p>
        <Button icon={<ExperimentOutlined />} loading={testing} onClick={handleTest}>
          Test connection
        </Button>
      </div>
    </div>
  );
};

export default FreeIpaSettingsTab;
